import React from "react";
import { Pressable, StyleSheet, Text as RNText } from "react-native";
import dayjs from "dayjs";
import { graphql } from "react-relay";
import { useFragment } from "react-relay/hooks";
import { View, Text } from "@components/atoms/Themed";
import Icon from "@components/atoms/Icon";
import Divider from "@components/atoms/Divider";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import type { NotificationListItem_notification$key as NotificationListItemFragmentKey } from "@generated/NotificationListItem_notification.graphql";

const notificationFragment = graphql`
  fragment NotificationListItem_notification on Notification {
    id
    message
    messagePreview
    influencerName
    seen
    createdAt
    url
    urlText
    campaignTitle
    advertiserName
    pushCampaignMessage {
      title
    }
  }
`;

type Props = {
  fragmentRef: NotificationListItemFragmentKey;
  onPress: (params: {
    id: string;
    seen: boolean;
    url: string | null;
  }) => void;
};

export default function NotificationListItem({
  fragmentRef,
  onPress,
}: Props): JSX.Element {
  const node = useFragment(notificationFragment, fragmentRef);
  const seen = node.seen === true;
  const hasInfluencerName =
    node.influencerName != null && node.influencerName !== "";
  const hasCampaignTitle =
    node.campaignTitle != null && node.campaignTitle !== "";
  const hasAdvertiserName =
    node.advertiserName != null && node.advertiserName !== "";
  // 旧アプリに詳細リンクを表示させないため、配信通知のリンク文言はアプリ側で補う。
  const pushCampaignTitle = node.pushCampaignMessage?.title ?? null;
  const urlText =
    node.urlText ?? (pushCampaignTitle != null ? "詳細を確認する" : null);
  return (
    <>
      <Pressable
        onPress={() =>
          onPress({ id: node.id, seen, url: node.url ?? null })
        }
        style={({ pressed }) => [styles.row, pressed && styles.pressed]}
      >
        <View style={styles.dotArea}>
          {!seen && (
            // ダークモード時の色上書き (Themed.Icon) を回避し、agency と同じ
            // オレンジドットを表示する。
            <Icon color={Colors.orange} name="circle" size={10} skipDarkModeColor />
          )}
        </View>
        <View style={styles.body}>
          {hasCampaignTitle && (
            <Text numberOfLines={1} style={styles.subline}>
              キャンペーン：{node.campaignTitle}
            </Text>
          )}
          {hasAdvertiserName && (
            <Text numberOfLines={1} style={styles.subline}>
              広告主名：{node.advertiserName}
            </Text>
          )}
          {hasInfluencerName && (
            <Text style={styles.subline}>
              インフルエンサー名：{node.influencerName}
            </Text>
          )}
          {pushCampaignTitle != null && (
            <Text numberOfLines={1} style={styles.title}>
              {pushCampaignTitle}
            </Text>
          )}
          <Text numberOfLines={2} style={styles.message}>
            {node.messagePreview ?? node.message ?? ""}
          </Text>
          <View style={styles.footer}>
            <Text style={styles.time}>
              {dayjs(node.createdAt).format("M月D日 HH:mm")}
            </Text>
            {urlText != null && urlText !== "" && (
              // Themed.Text はダークモードで色を強制上書きするため、
              // urlText (agency と同じ cyan リンク) は RNText で明示色指定する。
              <RNText style={styles.urlText}>{urlText}</RNText>
            )}
          </View>
        </View>
      </Pressable>
      <Divider color={Colors.gray20} height={1} />
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "flex-start",
  },
  pressed: {
    opacity: 0.7,
  },
  dotArea: {
    width: 20,
    paddingTop: 6,
    alignItems: "center",
    justifyContent: "flex-start",
  },
  body: { flex: 1 },
  subline: {
    ...Fonts.sr100,
    color: Colors.gray,
    marginBottom: 2,
  },
  title: {
    ...Fonts.lb100,
    marginBottom: 2,
  },
  message: {
    ...Fonts.mr100,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 4,
  },
  time: {
    ...Fonts.sr100,
    color: Colors.gray,
  },
  urlText: {
    ...Fonts.sb100,
    color: Colors.green,
  },
});
